import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useProcessing } from "../context/ProcessingContext";
import { CartContext } from "../context/CartContext";
import { Popover } from "bootstrap";
import useChatLogic from "../hooks/useChatLogic";
import QuestionComponent from "./QuestionTypes/QuestionComponent";
import SupplementSectionRenderer from "./SupplementSectionRenderer";
import ChatHighlights from "./ChatHighlights";
import Image from "./Image";
import Button from "./Button";
import FormCheck from "./FormCheck";
import FormRadio from "./FormRadio";
import { useModal } from "../context/ModalContext";
import { UserContext } from "../context/UserContext";
import { registerUserWithAnswers } from "../services/ChatService";
import { fireEvent } from "../utils/marketing";
import '../assets/styles/chat.css';
// import '../assets/styles/atendimento.css';
import arrowLeftGreen from '../assets/images/svg/arrowLeftGreen.svg';
import logoPurissimaBrown from '../assets/images/svg/logoPurissimaBrown.svg';
import estimatedTime from '../assets/images/svg/estimatedTime.svg';
import logoPurissima from '../assets/images/svg/unicornio.svg';
import fullLogo from "../assets/images/svg/logoFullVertical.svg";

const SECTION_NAME_BY_SUB_ID = {
  38: "Antes de começarmos",
  37: "Nutrição",
  24: "Equilíbrio digestivo e intestinal",
  23: "Movimento e exercício físico",
  22: "Sono e regeneração Noturna",
  21: "Saúde mental e emocional",
  28: "Saúde Masculina",
  29: "Saúde Feminina",
  45: "Sintomas Complementares",
};

const toEventSlug = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

const Chat = ({ grupoSlug }) => {
  const { sectionId } = useParams();
  const navigate = useNavigate();

  const { user } = useContext(UserContext);
  const usr_id = user?.usr_id;
  const formVar = [38, 46];
  const [accepted, setAccepted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveAttempts, setSaveAttempts] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [infoFree, setInfoFree] = useState(false);
  const [aplv, setAPLV] = useState(null);
  const { openModal, closeModal } = useModal();

  const {
    currentSectionIndex,
    currentSectionQuestions,
    currentGrouped,
    handleChangeAnswer,
    handleBlurAnswer,
    handleGroupedAnswerSubmission,
    isChatFinished,
    currentSectionMeta,
    progress,
    answers,
    isLoaded,
    analyzing,
    setCurrentSectionIndex,
    sectionOrder,
    validateSectionAndFinalizeIfLast,
    goToNextSection: goToNextSectionBase,
    goBackToPreviousSection: goBackToPreviousSectionBase
  } = useChatLogic(grupoSlug);

  const renewModalShownRef = useRef(false);
  const lastSectionEventRef = useRef({});

  const termsAcceptanceLabel =
    'Li e concordo com os <a href="#" data-action="terms"><strong>Termos de Uso</strong></a>.';

  const handleOpenTermsModal = useCallback(() => {
    openModal("terms", { title: "Termos e Condições Gerais de Uso" });
  }, [openModal]);

  const fireQuestionnaireSectionEvent = useCallback(
    (action, extraParams = {}) => {
      const subId = currentSectionMeta?.sub_id;
      if (!subId) return;

      const safeGroup = String(grupoSlug || "adulto");
      const friendlyName = SECTION_NAME_BY_SUB_ID?.[Number(subId)];
      const sectionSlug = friendlyName ? toEventSlug(friendlyName) : `sub_${String(subId)}`;
      const eventName = `questionario_${safeGroup}_secao_${sectionSlug}_${String(action)}`;

      // Dedup para evitar duplicar em React StrictMode e re-render imediato
      const dedupKey = `dl_dedup_${eventName}`;
      const now = Date.now();
      try {
        const lastTs = Number(sessionStorage.getItem(dedupKey) || 0);
        if (now - lastTs < 1000) return;
        sessionStorage.setItem(dedupKey, String(now));
      } catch {
        // ignore
      }

      // Dedup adicional por instância do componente
      const memKey = `${eventName}|${String(sectionId ?? "")}|${String(currentSectionIndex ?? "")}`;
      if (lastSectionEventRef.current[memKey]) return;
      lastSectionEventRef.current[memKey] = true;

      fireEvent(eventName, {
        questionnaire_group: safeGroup,
        section_name: friendlyName,
        section_slug: sectionSlug,
        sub_id: Number(subId),
        section_id: sectionId != null ? String(sectionId) : undefined,
        section_index: typeof currentSectionIndex === "number" ? currentSectionIndex : undefined,
        progress: typeof progress === "number" ? progress : undefined,
        ...extraParams,
      });
    },
    [currentSectionMeta?.sub_id, grupoSlug, sectionId, currentSectionIndex, progress]
  );

  // Funções wrapper para ativar overlay de processamento ao navegar
  const { isProcessing, setIsProcessing } = useProcessing();
  const [pendingNav, setPendingNav] = useState(false);


  // Funções de navegação: overlay só ativa após validação
  const goToNextSection = () => {
    setIsProcessing(true);
    setPendingNav(true);
    goToNextSectionBase();
  };
  const goBackToPreviousSection = () => {
    setIsProcessing(true);
    setPendingNav(true);
    goBackToPreviousSectionBase();
  };

  // Desativa overlay após carregamento da nova seção
  useEffect(() => {
    if (pendingNav && isLoaded) {
      setIsProcessing(false);
      setPendingNav(false);
    }
  }, [isLoaded, pendingNav, setIsProcessing]);

  // Evento: visualização da seção atual (1 por seção)
  useEffect(() => {
    if (!isLoaded) return;
    if (!currentSectionMeta?.sub_id) return;
    fireQuestionnaireSectionEvent("view");
  }, [isLoaded, currentSectionMeta?.sub_id, currentSectionIndex, sectionId, fireQuestionnaireSectionEvent]);

  const location = useLocation();
  // Desativa overlay se sair da rota do questionário, mas só se setIsProcessing estiver definido
  useEffect(() => {
    if (typeof setIsProcessing === 'function' && !location.pathname.startsWith('/questionario')) {
      setIsProcessing(false);
    }
  }, [location.pathname, setIsProcessing]);

  // Exibe modal de decis	o (renovar x manter anamnese) quando houver anamnese prévia carregada
  useEffect(() => {
    if (renewModalShownRef.current) return;
    if (!isLoaded) return;
    if (currentSectionIndex !== 0) return;

    const hasAnswers = answers && Object.keys(answers).length > 0;
    if (!hasAnswers) return;

    let hasCurrentAnamnesis = false;
    try {
      const currentAnam = localStorage.getItem("currentAnamnesis");
      hasCurrentAnamnesis = !!currentAnam;
    } catch {
      hasCurrentAnamnesis = false;
    }

    // Lógica de disparo: flag do localStorage (Minha Conta) ou querystring
    let shouldShow = false;
    try {
      const flag = localStorage.getItem("showAnamnesisRenewModal");
      const params = new URLSearchParams(location.search || "");
      const renewParam = params.get("renew") || params.get("renew_anamnese");
      shouldShow = flag === "1" || renewParam === "1";
    } catch {
      shouldShow = false;
    }

    if (!shouldShow || !hasCurrentAnamnesis) return;

    const type =
      grupoSlug === "my-baby" || grupoSlug === "my-kids" || grupoSlug === "adulto"
        ? grupoSlug
        : "adulto";

    openModal("anamnesisRenew", { type });
    renewModalShownRef.current = true;

    try {
      localStorage.removeItem("showAnamnesisRenewModal");
    } catch { }
  }, [isLoaded, currentSectionIndex, answers, grupoSlug, location.search, openModal]);

  // Persistência dos checkboxes: usar answers do hook

  const leiteMaterno = answers['leiteMaterno']?.answer === true;
  const indicacaoIdade = answers['indicacaoIdade']?.answer === true;

  // Garante que o botão nunca fique travado após navegação de seção
  useEffect(() => {
    setIsSaving(false);
  }, [currentSectionIndex]);

  // Cleanup: sempre desativa overlay ao desmontar o Chat
  useEffect(() => {
    return () => {
      setIsProcessing(false);
    };
  }, [setIsProcessing]);

  // Garante que o overlay nunca fique travado após finalizar
  useEffect(() => {
    if (isChatFinished && (isProcessing || analyzing)) {
      setIsProcessing(false);
    }
  }, [isChatFinished, isProcessing, analyzing, setIsProcessing]);

  useEffect(() => {
    if (grupoSlug !== "my-baby") {
      setAPLV("false"); // Se não for My Baby, assume que não tem APLV (string, para bater com as comparações "aplv == 'false'")
    }
  }, [grupoSlug]);

  useEffect(() => {

    if (aplv == "true") {
      // grupoSlug === "my-baby" ? setLeiteMaterno(false) : setLeiteMaterno(true);
      const data = {
        icon: fullLogo,
        // progress: 95,
        title: "ATENÇÃO - PRODUTO NÃO INDICADO",
        content: {
          description: `A suplementação Puríssima ${grupoSlug === "my-baby" ? "Baby" : "Kids"} não contém lactose, mas contém proteína do leite de vaca. Por isso, ela não é indicada para ${grupoSlug === "my-baby" ? "bebês" : "crianças"} com alergia à proteína do leite (APLV).`,
          button: {
            label: "Voltar para a home",
            onClick: () => {
              window.location.href = "/";
            }
          }
        }
      }
      openModal('confirmation', data);
    }
  }, [aplv, grupoSlug]);

  // Se o usuário não marcar os termos leiteMaterno e indicacaoIdade, desabilita o botão
  const babyFree = grupoSlug === "my-baby" ? leiteMaterno && indicacaoIdade && aplv == "false" : true;
  const kidsFree = grupoSlug === "my-kids" ? indicacaoIdade && aplv == "false" : true;

  // ...existing code...

  // Sincronizar seção da URL com o índice atual SEMPRE que sectionId, sectionOrder ou currentSectionIndex mudarem
  useEffect(() => {
    if (Array.isArray(sectionOrder) && sectionOrder.length > 0 && sectionId) {
      const idx = sectionOrder.findIndex(id => String(id) === String(sectionId));
      if (idx >= 0 && idx !== currentSectionIndex) {
        setCurrentSectionIndex(idx);
      }
    }
  }, [sectionId, sectionOrder, currentSectionIndex]);

  // Navegar para próxima seção via rota
  const handleSectionSubmission = async () => {
    if (isSaving) {
      console.log('Bloqueado: isSaving true');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      // Detecta se é a última seção
      const isLastSection = Array.isArray(sectionOrder) && currentSectionIndex === sectionOrder.length - 1;
      const valid = await validateSectionAndFinalizeIfLast(isLastSection, setIsProcessing);
      if (!valid) {
        console.log('Validação falhou, não avança.');
        setIsSaving(false);
        setIsProcessing(false); // Garante que overlay some se não avançar
        return;
      }

      // Evento: submissão/avanço da seção atual (sem enviar respostas)
      fireQuestionnaireSectionEvent("submit", { is_last_section: !!isLastSection });

      if (isLastSection) {
        // O salvamento e navegação já são tratados pelo hook
        setSaveAttempts(0);
        return;
      }
      if (Array.isArray(sectionOrder)) {
        console.log('Avançando seção:', currentSectionIndex, 'de', sectionOrder.length);
      }
      // Avança usando o hook (overlay só ativa aqui)
      goToNextSection();
      setSaveAttempts(0);
      // setIsSaving(false) será chamado após overlay sumir
    } catch (error) {
      console.error("Erro ao salvar anamnese:", error);
      setSaveError(error.message || "Erro ao salvar anamnese. Tente novamente.");
      setSaveAttempts(prev => prev + 1);
      setIsSaving(false);
      setIsProcessing(false); // Garante que overlay some em erro
    }
  };




  const handleSaveAnamnesis = async () => {
    console.log('Iniciando salvamento da anamnese...');
    if (isSaving) return; // Evita múltiplas chamadas simultâneas

    setIsSaving(true);
    setSaveError(null);

    try {
      await registerUserWithAnswers({
        usr_id,
        setIsProcessing: (value) => {
          setIsProcessing(value);
          if (!value) setIsSaving(false); // Para de indicar que está salvando quando termina
        }
      });
      setSaveAttempts(0); // Reset counter on success
    } catch (error) {
      console.error("Erro ao salvar anamnese:", error);
      setSaveError(error.message || "Erro ao salvar anamnese. Tente novamente.");
      setSaveAttempts(prev => prev + 1);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (
      isChatFinished &&
      saveAttempts === 0 &&
      Array.isArray(sectionOrder) &&
      currentSectionIndex === sectionOrder.length - 1
    ) {
      console.log("Finalizou o questionário.");
      handleSaveAnamnesis();
    }
  }, [isChatFinished, saveAttempts, currentSectionIndex, Array.isArray(sectionOrder) ? sectionOrder.length : 0]);

  // Definir infoFree true quando os campos do subject = 38 estiverem preenchidos corretamente
  useEffect(() => {
    if (currentSectionMeta?.sub_id === 38) {
      const requiredSymIds = [252, 253, 254, 255, 256]; // IDs das perguntas obrigatórias
      const allAnswered = requiredSymIds.every(symId => answers.hasOwnProperty(symId) && answers[symId] !== "");
      setInfoFree(allAnswered);
    } else {
      setInfoFree(true); // Se não estiver na seção 38, considera como true
    }
  }, [answers, currentSectionMeta]);

  // Identificar se paciente é vegano ou intolerante à lactose
  useEffect(() => {
    // console.log('Verificando respostas para vegano/intolerante à lactose...', answers[585]?.answer);
    const resposta = answers[585]?.answer;
    if (
      currentSectionMeta?.sub_id === 37 &&
      Array.isArray(resposta) &&
      (resposta.includes(538) || resposta.includes(539))
    ) {
      openModal('confirmation', {
        icon: fullLogo,
        title: "ATENÇÃO",
        content: {
          description: "<p>A Puríssima foi criada sob a filosofia de constante evolução, e pretendemos nos próximos meses termos opção de proteína vegana em nossas fórmulas, visto que utilizamos alguns componentes excepcionais de origem animal (Whey Isolado Premium, Proteína de Colágeno, Vitamina D, Lactoferrina).</p><p>Até lá, ainda não conseguimos atender suas restrições, pois nossos testes sensoriais não ficaram à altura daquilo que gostamos de oferecer. Contamos com sua paciência e compreensão. Como você deixou seu contato, avisaremos assim que estiver disponível.</p><p>Você pode prosseguir com sua anamnese com objetivo de obter seu Mapa de Saúde, que é gratuito.</p>",
          button: {
            label: "Continuar",
            onClick: () => { closeModal(); }
          }
        }
      });
    } else if (
      currentSectionMeta?.sub_id === 37 &&
      Array.isArray(resposta) &&
      (resposta.includes(540) || resposta.includes(541))
    ) {
      openModal('confirmation', {
        icon: fullLogo,
        title: "ATENÇÃO",
        content: {
          description: "<p>Em nenhuma das fórmulas da Puríssima utilizamos ingredientes que contenham glúten, lactose ou caseína.</p><p>Nosso Whey Premium, que faz parte da matriz nutricional das fórmulas, é certificado como livre de lactose ou caseína, portanto muito bem tolerado por quem tem intolerância a estes componentes.</p>",
          button: {
            label: "Continuar",
            onClick: () => { closeModal(); }
          }
        }
      });
    }
  }, [answers[585]?.answer]);

  useEffect(() => {
    const popoverTop = document.querySelectorAll(".progressThumb.thumbTop");
    popoverTop.forEach((trigger) => {
      new Popover(trigger, {
        trigger: "hover focus",
        placement: "top",
        content: `${progress}%`
      });
    });

    const popoverBottom = document.querySelectorAll(".progressThumb.thumbBottom");
    popoverBottom.forEach((trigger) => {
      new Popover(trigger, {
        trigger: "hover focus",
        placement: "bottom",
        content: `${progress}%`
      });
    });
  }, [progress]);

  // Mensagem automática se a resposta for “Sim” (APLV):
  //           A suplementação Puríssima Baby não contém lactose, mas contém proteína do leite de vaca. Por isso, ela não é indicada para bebês com alergia à proteína do leite (APLV).

  //           Agradecemos o seu interesse — em breve traremos novidades para este perfil.

  return (
    <div className={`Chat ${grupoSlug != undefined ? `custom-${grupoSlug}` : ''}`}>
      {/* style={{ backgroundImage: grupoSlug === 'my-baby' ? `url(../assets/images/questionario-baby-bottom.png)` : 'none' }}  */}
      {(isProcessing || analyzing) && currentSectionMeta?.sub_id !== 38 && (
        <div className="processing-message">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 mb-4 text-center">
                <img src={logoPurissima} alt="logoPurissima" />
              </div>
              <div className="col-auto text-center">
                <h4 className="text-white text-uppercase">
                  <strong>Analisando respostas<br /> Por favor, aguarde.</strong>
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 chat-section-cover">
            <Image image={currentSectionMeta?.groupCover} alt={currentSectionMeta?.groupTitle} />
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 px-0">
            <div className="progress" role="progressbar" aria-label="Basic example" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
              <div className="progress-bar" style={{ width: `${progress}%` }}></div>
              <div className="progressThumb thumbBottom"
                data-bs-custom-class="custom-popover"
                data-bs-content={`${progress}%`}
                style={{ left: `${progress}%` }}>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="chatHive position-relative">
        <div className={`container-fluid ${formVar.includes(currentSectionMeta?.sub_id) ? "personal-info" : {}}`}>
          <div className="row justify-content-center">
            <div className="col-11 col-lg-12 chat-section-title">
              {isLoaded ? (
                <>
                  <h1 dangerouslySetInnerHTML={{ __html: `<strong>${currentSectionMeta?.groupTitle}</strong>` }} />
                  {currentSectionMeta?.groupDescription && (
                    <div dangerouslySetInnerHTML={{ __html: currentSectionMeta?.groupDescription }} className="chat-section-description" />
                  )}
                </>
              ) : (
                <h3 className="text-center text-uppercase pb-5">
                  <strong>Carregando perguntas. Por favor, aguarde...</strong>
                </h3>
              )}
            </div>
          </div>
        </div>

        <div className={`container-fluid ${formVar.includes(currentSectionMeta?.sub_id) ? "personal-info" : {}}`}>
          <div className="row justify-content-center">
            <div className="col-11 col-lg-12">
              {currentSectionMeta?.sub_id === 39 ? (
                <SupplementSectionRenderer
                  questions={currentSectionQuestions}
                  answers={answers}
                  onChange={handleChangeAnswer}
                  onBlur={handleBlurAnswer}
                />
              ) : (
                <QuestionComponent
                  questions={currentSectionQuestions}
                  // questions={currentSectionQuestions.filter(q => {
                  //   if (usr_id && (q.sym_id === 259 || q.sym_id === 466)) return false;
                  //   return true;
                  // })}
                  groupedQuestions={currentGrouped?.questions || []}
                  onChange={handleChangeAnswer}
                  onBlur={handleBlurAnswer}
                  answers={answers}
                />
              )}
            </div>
            {isLoaded && currentSectionMeta?.sub_id === 38 && (
              <div className="col-11 col-lg-12 dados-confidenciais">
                <div className="pt-4 px-lg-4">
                  <small><strong>Seus dados são confidenciais.</strong><br />
                    Todas as informações compartilhadas aqui serão tratadas com absoluto sigilo e utilizadas exclusivamente para personalizar sua experiência com a Puríssima.</small>
                </div>
              </div>
            )}

            {currentSectionIndex === 0 && isLoaded && (grupoSlug === "my-baby" || grupoSlug === "my-kids") && (
              <div className="col-11 my-4 dados-confidenciais">
                <small><strong>Antes de começarmos, vamos verificar uma condição fundamental para garantir a segurança e a adequação da suplementação {grupoSlug === "my-baby" ? "ao seu bebê" : "à sua criança"}.</strong></small>
                <p className="mt-2"><small>{grupoSlug === "my-baby" ? "Seu bebê tem alergia confirmada à proteína do leite de vaca (APLV) e, portanto, precisa excluir qualquer traço de leite?" : "A criança tem alergia confirmada à proteína do leite de vaca (APLV) e, por isso, precisa evitar qualquer traço de leite?"}</small></p>
                <div className="row mt-3">
                  <div className="col-auto">
                    <FormRadio
                      value="true"
                      onChange={e => setAPLV(e.target.value)}
                      checked={aplv == "true"}
                      name="aplvRadio"
                      label="Sim"
                    />
                  </div>
                  <div className="col-auto">
                    <FormRadio
                      value="false"
                      onChange={e => setAPLV(e.target.value)}
                      checked={aplv == "false"}
                      name="aplvRadio"
                      label="Não"
                    />
                  </div>
                </div>
              </div>
            )}
            {/* Conteúdo customizado para My Baby */}
            {currentSectionIndex === 0 && isLoaded && grupoSlug === "my-baby" && (
              <>
                <div className="col-11 my-4">
                  <div className="box-degrade p-3">
                    <FormCheck
                      onChange={(e) => handleChangeAnswer('leiteMaterno', e.target.checked, { groupId: null, sub_id: null })}
                      checked={leiteMaterno}
                      name="leite-materno"
                      label="Estou ciente de que o Suplemento Baby Puríssima complementa a alimentação e não substitui o leite materno." />
                  </div>
                  <div className="box-degrade mt-3 p-3">
                    <FormCheck
                      onChange={(e) => handleChangeAnswer('indicacaoIdade', e.target.checked, { groupId: null, sub_id: null })}
                      checked={indicacaoIdade}
                      name="indicacao-idade"
                      label="Estou ciente de que o produto é indicado apenas para bebês de 12 a 36 meses." />
                  </div>
                </div>
              </>
            )}

            {/* Conteúdo customizado para My Kids */}
            {currentSectionIndex === 0 && isLoaded && grupoSlug === "my-kids" && (
              <>
                <div className="col-11 my-4">
                  <div className="box-degrade p-3">
                    <FormCheck
                      onChange={(e) => handleChangeAnswer('leiteMaterno', e.target.checked, { groupId: null, sub_id: null })}
                      checked={leiteMaterno}
                      name="leite-materno"
                      label="Estou ciente de que o Suplemento My Kids Puríssima complementa a alimentação e não substitui refeições completas." />
                  </div>
                  <div className="box-degrade mt-3 p-3">
                    <FormCheck
                      onChange={(e) => handleChangeAnswer('indicacaoIdade', e.target.checked, { groupId: null, sub_id: null })}
                      checked={indicacaoIdade}
                      name="indicacao-idade"
                      label="Estou ciente de que o produto é indicado apenas para crianças de 3 a 12 anos." />
                  </div>
                </div>
              </>
            )}

            {isLoaded && !currentSectionMeta?.nextGroupTitle && (
              <div className="col-11 col-lg-12">
                <div className="box-degrade p-4 mt-5">
                  <FormCheck
                    onChange={(e) => setAccepted(e.target.checked)}
                    checked={accepted}
                    name="terms-confirm"
                    label="<strong>Declaro que as informações fornecidas são verdadeiras</strong> e compreendo que esta anamnese será avaliada por profissional de saúde habilitado, que poderá emitir prescrição personalizada. Autorizo o encaminhamento do receituário à Puríssima para aviamento das minhas fórmulas personalizadas." />
                </div>
              </div>
            )}
          </div>
        </div>

        <ChatHighlights sub_id={currentSectionMeta?.sub_id} />
      </div>

      {/* Exibir erro de salvamento */}
      {saveError && (
        <div className="container mt-5">
          <div className="row justify-content-center">
            <div className="col-11 col-lg-8">
              <div className="alert text-center">
                <h5 className="text-uppercase"><strong>Erro ao finalizar anamnese</strong></h5>
                {/* <p>{saveError}</p> */}
                <small>Clique novamente em "FINALIZAR ANAMNESE" para tentar novamente.</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoaded && (
        <div className="container chat-navigation">
          <div className="row justify-content-center">
            {currentSectionMeta?.sub_id === 38 && (
              <div className="col-auto mb-3">
                <div className="box-degrade p-3">
                  <FormCheck
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    checked={acceptedTerms}
                    name="terms-acceptance"
                    label={termsAcceptanceLabel}
                    onLabelClick={(e) => {
                      const link = e?.target?.closest?.('a[data-action="terms"]');
                      if (!link) return;
                      e.preventDefault();
                      e.stopPropagation();
                      handleOpenTermsModal();
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="row justify-content-center">
            <div className="col-10 col-lg-auto">
              <Button
                classes="w-100 justify-content-center"
                label={
                  currentSectionMeta?.nextGroupTitle ? (
                    <>
                      <span className="nextButton m-0"><strong className="d-block d-lg-inline">Próxima Etapa </strong></span>
                      <span className="mx-2 d-none d-lg-inline-flex">|</span>
                      <span style={{ fontWeight: 500 }}>{currentSectionMeta.nextGroupTitle}</span>
                    </>
                  ) : (
                    <>
                      {isSaving ? "Salvando..." : "Finalizar anamnese"}
                    </>
                  )
                }
                onClick={saveError ? handleSaveAnamnesis : handleSectionSubmission}
                disabled={
                  (() => {
                    const disabled =
                      isSaving ||
                      !infoFree ||
                      !babyFree ||
                      !kidsFree ||
                      (currentSectionMeta?.sub_id === 38 && !acceptedTerms) ||
                      (isLoaded && !currentSectionMeta?.nextGroupTitle && !accepted);
                    // console.log('[BOTAO] isSaving:', isSaving, '| infoFree:', infoFree, '| babyFree:', babyFree, '| kidsFree:', kidsFree, '| accepted:', accepted, '| isLoaded:', isLoaded, '| nextGroupTitle:', currentSectionMeta?.nextGroupTitle);
                    return disabled;
                  })()
                }
              />
            </div>
            {currentSectionIndex !== 0 && (
              <div className="col-12">
                <button className="btn back-btn" onClick={goBackToPreviousSection}>
                  <img className="me-2" src={arrowLeftGreen} alt="arrowLeftGreen" />Editar respostas anteriores
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-8 text-center chatLogoPurissima">
            <img src={logoPurissimaBrown} alt="logo Purissima Brown" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;